import type {
  AnswersPostRequest,
  AnswersPostResponse,
} from "../../../api/src/users/answers/route";
// Need to use the React-specific entry point to import createApi
import config from "../config";
import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareHeaders } from "./utils";
import { Answer } from "../../../api/src/types";
import {
  createQuestionnaireSectionDetailsTagId,
  createQuestionnaireSectionsTagId,
  questionnaireApi,
} from "./questionnaire";
import { createSerializedFetchBaseQuery } from "./serializedFetchBaseQuery";
import type { AnswersWithAttorneyRestrictionsGetResponse } from "../../../api/src/users/answers/route";
import { AnswerMutationSource } from "../../../api/src/users/answers/answer-mutation-contract";
import type {
  AlignmentGetResponse,
  AlignmentHistoryGetResponse,
} from "../../../api/src/users/alignments/route";

import { documentsApi } from "./documents";
import { clearQuestionAnswerTreeInCache } from "./answerTreeCache";
import {
  buildSerializedAnswerMutationHeaders,
  buildUpsertAnswerRequestBody,
  createCleanSlateTracker,
  patchQueryDataIfPresent,
  runOptimisticAnswerMutation,
} from "./answerMutationHelpers";

export type AlignmentItem = AlignmentGetResponse["items"][number];
export type AlignmentHistoryItem = AlignmentHistoryGetResponse["items"][number];
type AlignmentHistoryCache = AlignmentHistoryGetResponse & {
  hasLoadedOlderPage?: boolean;
};

function mergeAlignmentHistoryItems(
  current: AlignmentHistoryItem[],
  incoming: AlignmentHistoryItem[],
) {
  const itemsById = new Map(current.map((item) => [item.id, item]));
  for (const item of incoming) itemsById.set(item.id, item);
  return [...itemsById.values()].sort(
    (left, right) =>
      new Date(right.occurredAt).getTime() -
        new Date(left.occurredAt).getTime() || right.id.localeCompare(left.id),
  );
}

export function mergeAlignmentHistoryPage(
  current: AlignmentHistoryCache,
  incoming: AlignmentHistoryGetResponse,
  isOlderPage: boolean,
) {
  if (
    current.historySource !== incoming.historySource ||
    (!isOlderPage && !current.hasLoadedOlderPage)
  ) {
    Object.assign(current, incoming, { hasLoadedOlderPage: false });
    return;
  }
  current.items = mergeAlignmentHistoryItems(current.items, incoming.items);
  if (isOlderPage) {
    current.nextCursor = incoming.nextCursor;
    current.hasLoadedOlderPage = true;
  }
}

interface AnswerUpsertMutationResponse {
  kind: "no-op" | "create" | "update" | "delete";
  answer?: Answer;
  deletedChildQuestionIds: string[];
}

const answersCleanSlateTracker = createCleanSlateTracker({
  createKey: (params: {
    userId: string;
    documentId: string;
    questionId: string;
  }): string => {
    return `answers-clean-slate:${params.userId}:${params.documentId}:${params.questionId}`;
  },
});

export const answersApi = createApi({
  reducerPath: "answersApi",
  baseQuery: createSerializedFetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["answers", "documents", "alignments", "alignment-history"],
  endpoints: (builder) => ({
    updateWeddingDate: builder.mutation<
      AnswersPostResponse,
      { userId: string; documentId: string; date: string }
    >({
      query: ({ userId, documentId, date }) => ({
        url: `/users/${userId}/answers`,
        method: "POST",
        params: { documentId, questionId: config.weddingDateQuestionId },
        body: {
          valueTyped: date,
          confirmedWeddingDate: true,
          source: AnswerMutationSource.QUESTIONNAIRE,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        } satisfies AnswersPostRequest,
        headers: buildSerializedAnswerMutationHeaders({
          serializeBy: documentId,
        }),
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          questionnaireApi.util.invalidateTags([
            "QuestionnaireSections",
            "QuestionGroup",
          ]),
        );
        dispatch(
          documentsApi.util.invalidateTags([
            "documents",
            "regeneration-status",
            "notarizations",
          ]),
        );
      },
      invalidatesTags: ["answers", "alignments", "alignment-history"],
    }),
    getAnswer: builder.query<
      Answer[],
      { questionId?: string; userId: string; documentId: string }
    >({
      query: ({ questionId, userId, documentId }) => ({
        url: `/users/${userId}/answers`,
        params: { documentId, ...(questionId ? { questionId } : {}) },
      }),
      providesTags: ["answers"],
    }),
    upsertAnswer: builder.mutation<
      AnswerUpsertMutationResponse,
      {
        questionId: string;
        userId: string;
        documentId: string;
        sectionId: string;
        serializeDebounceMs?: number;
        valueTyped?: string | null;
        questionOptionIds?: string[];
        source: AnswerMutationSource;
      }
    >({
      query: ({
        questionId,
        userId,
        documentId,
        serializeDebounceMs,
        valueTyped,
        questionOptionIds,
        source,
      }) => ({
        url: `/users/${userId}/answers`,
        method: "POST",
        params: { questionId, documentId },
        body: {
          ...buildUpsertAnswerRequestBody({
            valueTyped,
            questionOptionIds,
            forceCleanSlate: answersCleanSlateTracker.has({
              userId,
              documentId,
              questionId,
            }),
          }),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          source,
        } satisfies AnswersPostRequest,
        headers: buildSerializedAnswerMutationHeaders({
          serializeBy: documentId,
          pruneBy: `${questionId}-POST`,
          serializeDebounceMs,
        }),
      }),
      async onQueryStarted(
        {
          questionId,
          userId,
          documentId,
          valueTyped,
          questionOptionIds,
          sectionId,
        },
        api,
      ) {
        const sectionGroupsArgs = {
          docId: documentId,
          section: sectionId,
          userId,
        } as const;

        await runOptimisticAnswerMutation({
          dispatch: api.dispatch,
          getState: () => api.getState(),
          queryFulfilled: api.queryFulfilled,
          cache: {
            args: sectionGroupsArgs,
            selectQueryState: (state, args) =>
              questionnaireApi.endpoints.getQuestionnaireSectionGroupsByDocumentId.select(
                args,
              )(state),
            updateQueryData: (args, updater) =>
              questionnaireApi.util.updateQueryData(
                "getQuestionnaireSectionGroupsByDocumentId",
                args,
                updater,
              ),
          },
          optimisticTreeUpdate: {
            questionId,
            valueTyped,
            questionOptionIds,
            createOptimisticAnswer: ({
              questionId,
              normalizedValueTyped,
              normalizedOptionIds,
              now,
            }) => {
              const tempId = `temp-${now.getTime()}`;

              return {
                id: tempId,
                questionId,
                userId,
                documentId,
                valueTyped: normalizedValueTyped ?? null,
                deletedAt: null,
                createdAt: now.toISOString(),
                updatedAt: now.toISOString(),
                createdById: userId,
                updatedById: userId,
                answerQuestionOptions: normalizedOptionIds.map((optionId) => ({
                  questionOptionId: optionId,
                  answerId: tempId,
                  isDefault: false,
                  createdAt: now.toISOString(),
                  updatedAt: now.toISOString(),
                })),
              };
            },
            createAnswerQuestionOption: ({ answerId, optionId, now }) => ({
              questionOptionId: optionId,
              answerId,
              isDefault: false,
              createdAt: now.toISOString(),
              updatedAt: now.toISOString(),
            }),
          },
          cleanSlateKey: { userId, documentId, questionId },
          cleanSlateTracker: answersCleanSlateTracker,
          shouldUndoOnError: () => false,
          onError: ({ error, dispatch, state, cacheArgs }) => {
            patchQueryDataIfPresent({
              dispatch,
              state,
              args: cacheArgs,
              selectQueryState: (state, args) =>
                questionnaireApi.endpoints.getQuestionnaireSectionGroupsByDocumentId.select(
                  args,
                )(state),
              updateQueryData: (args, updater) =>
                questionnaireApi.util.updateQueryData(
                  "getQuestionnaireSectionGroupsByDocumentId",
                  args,
                  updater,
                ),
              updater: (draft) => {
                clearQuestionAnswerTreeInCache({
                  draft,
                  parentQuestionId: questionId,
                });
              },
            });
            console.error("Failed to upsert answer:", error);
          },
        });

        const questionnaireTags = [
          {
            type: "QuestionnaireSections" as const,
            id: createQuestionnaireSectionDetailsTagId({
              userId,
              section: sectionId,
            }),
          },
          {
            type: "QuestionnaireSections" as const,
            id: createQuestionnaireSectionsTagId({
              userId,
              includeStatus: true,
            }),
          },
          { type: "QuestionOptionVisibility" as const },
        ];
        api.dispatch(questionnaireApi.util.invalidateTags(questionnaireTags));
        api.dispatch(
          documentsApi.util.invalidateTags([
            "documents",
            "regeneration-status",
            "notarizations",
          ]),
        );
      },
      invalidatesTags: ["answers", "alignments", "alignment-history"],
    }),
    getAnswersWithAttorneyRestrictions: builder.query<
      AnswersWithAttorneyRestrictionsGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/answers/attorney-restrictions`,
        params: { documentId },
      }),
      providesTags: ["answers"],
    }),
    keepAlignment: builder.mutation<
      { success: true },
      {
        userId: string;
        documentId: string;
        questionId: string;
        revision: string;
      }
    >({
      query: ({ userId, documentId, questionId, revision }) => ({
        url: `/users/${userId}/documents/${documentId}/alignments/${questionId}/keep`,
        method: "POST",
        body: { revision },
      }),
      invalidatesTags: ["alignments", "alignment-history"],
    }),
    getAlignments: builder.query<
      AlignmentGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/documents/${documentId}/alignments`,
      }),
      providesTags: ["alignments"],
    }),
    getAlignmentHistory: builder.query<
      AlignmentHistoryCache,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/documents/${documentId}/alignments/history`,
      }),
      merge: (current, incoming) => {
        mergeAlignmentHistoryPage(current, incoming, false);
      },
      providesTags: ["alignment-history"],
    }),
  }),
});

export const {
  useGetAnswerQuery,
  useUpdateWeddingDateMutation,
  useUpsertAnswerMutation,
  useGetAnswersWithAttorneyRestrictionsQuery,
  useKeepAlignmentMutation,
  useGetAlignmentsQuery,
  useGetAlignmentHistoryQuery,
} = answersApi;

export const { useLoadAlignmentHistoryPageMutation } =
  answersApi.injectEndpoints({
    endpoints: (builder) => ({
      loadAlignmentHistoryPage: builder.mutation<
        AlignmentHistoryGetResponse,
        { userId: string; documentId: string; cursor: string }
      >({
        query: ({ userId, documentId, cursor }) => ({
          url: `/users/${userId}/documents/${documentId}/alignments/history`,
          method: "GET",
          params: { cursor },
        }),
        async onQueryStarted({ userId, documentId }, api) {
          try {
            const { data } = await api.queryFulfilled;
            api.dispatch(
              answersApi.util.updateQueryData(
                "getAlignmentHistory",
                { userId, documentId },
                (current) => {
                  mergeAlignmentHistoryPage(current, data, true);
                },
              ),
            );
          } catch {
            return;
          }
        },
      }),
    }),
  });
